import type { ReactNode } from 'react';
import { useAuth } from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';
import type { Permission } from '@/api/types';

/**
 * UI-side hiding ONLY (Part C: "the UI hides actions a role can't perform
 * as a courtesy, but the backend permission matrix remains the real
 * enforcement"). Wrapping a route or an action button in this never
 * replaces the server's requirePermission() call - it exists purely so a
 * SUPPORT user is not shown a "New tariff rate" button it would get a 403
 * from anyway.
 */
export function RequirePermission({
  permission,
  children,
  fallback = null,
}: {
  permission: Permission;
  children: ReactNode;
  fallback?: ReactNode;
}): JSX.Element {
  const { hasPermission } = useAuth();
  if (!hasPermission(permission)) return <>{fallback}</>;
  return <>{children}</>;
}

export function PermissionDenied({ reason }: { reason: string }): JSX.Element {
  const t = useT();
  return (
    <div className="empty-state" role="alert">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('common.noAccess')}</div>
      <div>{reason}</div>
    </div>
  );
}
